import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { 
  Heart, 
  HeartOff,
  Calendar,
  MapPin,
  Search,
  Loader2,
  Ticket
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function LikedEvents() {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unlikingId, setUnlikingId] = useState(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
      return;
    }
    
    if (!authLoading && isAuthenticated) {
      fetchLikedEvents();
    }
  }, [authLoading, isAuthenticated]);

  const fetchLikedEvents = async () => {
    try {
      const res = await axios.get(`${API_URL}/user/liked-events`);
      setEvents(res.data || []);
    } catch (error) {
      console.error('Error fetching liked events:', error);
      toast.error('Failed to load liked events');
    } finally {
      setLoading(false);
    }
  };

  const handleUnlike = async (eventId, eventTitle) => {
    setUnlikingId(eventId);
    try {
      await axios.delete(`${API_URL}/events/${eventId}/like`);
      setEvents(events.filter(e => e.id !== eventId));
      toast.success(`Removed "${eventTitle}" from liked events`);
    } catch (error) {
      toast.error('Failed to unlike event');
    } finally {
      setUnlikingId(null);
    }
  };

  const formatEventDate = (event) => {
    if (event.date_display) {
      return event.date_display;
    }
    if (event.event_date) {
      try {
        return format(parseISO(event.event_date), 'MMM d, yyyy');
      } catch {
        return event.event_date;
      }
    }
    return 'Date TBA';
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Heart className="w-8 h-8 text-red-500 fill-red-500" />
            <h1 className="font-heading font-bold text-3xl">Liked Events</h1>
          </div>
          <p className="text-muted-foreground">
            Events you've saved • {events.length} event{events.length !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Events List */}
        {events.length === 0 ? (
          <Card className="bg-card border-white/10">
            <CardContent className="py-12 text-center">
              <Heart className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="font-heading font-semibold text-lg mb-2">
                No liked events yet
              </h3>
              <p className="text-muted-foreground mb-6">
                Like events to save them here and get quick access
              </p>
              <Button onClick={() => navigate('/events')}>
                <Search className="w-4 h-4 mr-2" />
                Browse Events
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {events.map((event) => (
              <Card 
                key={event.id} 
                className="bg-card border-white/10 hover:border-primary/30 transition-colors overflow-hidden"
                data-testid={`liked-event-${event.id}`}
              >
                {/* Event Image */}
                <div 
                  className="h-40 bg-cover bg-center cursor-pointer"
                  style={{ backgroundImage: `url(${event.image_url || 'https://customer-assets.emergentagent.com/job_creator-signup-4/artifacts/7bii36vj_ChatGPT%20Image%20Mar%2031%2C%202026%2C%2008_44_35%20PM.png'})` }}
                  onClick={() => navigate(`/events/${event.id}`)}
                />
                
                <CardContent className="p-4">
                  {/* Creator Info */}
                  {event.creator && (
                    <div 
                      className="flex items-center gap-2 mb-3 cursor-pointer"
                      onClick={() => navigate(`/creator/${event.creator.username || event.influencer_id}`)}
                    >
                      <Avatar className="w-6 h-6">
                        <AvatarImage src={event.creator.avatar_url} />
                        <AvatarFallback className="text-xs">
                          {event.creator.display_name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm text-muted-foreground hover:text-primary transition-colors">
                        {event.creator.display_name}
                      </span>
                    </div>
                  )}

                  {/* Event Title */}
                  <h3 
                    className="font-heading font-semibold text-lg mb-2 cursor-pointer hover:text-primary transition-colors line-clamp-2"
                    onClick={() => navigate(`/events/${event.id}`)}
                  >
                    {event.title}
                  </h3>

                  {/* Event Details */}
                  <div className="flex flex-wrap gap-3 text-sm text-muted-foreground mb-4">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatEventDate(event)}
                    </span>
                    {event.location && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {event.location}
                      </span>
                    )}
                  </div>

                  {/* Price & Actions */}
                  <div className="flex items-center justify-between">
                    <div>
                      {event.ticket_tiers && event.ticket_tiers.length > 0 && (
                        <p className="font-semibold text-primary">
                          From ${Math.min(...event.ticket_tiers.map(t => t.price || t.min_bid || 0))}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleUnlike(event.id, event.title)}
                        disabled={unlikingId === event.id}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        {unlikingId === event.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <HeartOff className="w-4 h-4 mr-1" />
                            Unlike
                          </>
                        )}
                      </Button>
                      <Button 
                        size="sm"
                        onClick={() => navigate(`/events/${event.id}`)}
                      >
                        <Ticket className="w-4 h-4 mr-1" />
                        Get Tickets
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
